import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import {
  duration,
  prerequisites,
  genericDiscipline,
} from "../../utilis/constants";
import { get_ApiManager, post_ApiManager } from "../ApiManager/ApiManager";
import swal from "sweetalert";
import { getIntakes } from "../Services/apiServices";
import { getCourseFormIntakeOptions } from "./courseIntakeOptions";
import { getProviderFormLayout } from "./providerFormLayout";
import ProviderFormMultiselect from "./ProviderFormMultiselect";
import "../../styles/tailwind.css";

const AddCourse = ({
  embedded = false,
  formId,
  providerId: providerIdProp,
  providerName: providerNameProp,
  category: categoryProp,
  providerCountry: providerCountryProp,
  onSuccess,
  onCancel,
  onSavingChange,
}) => {
  const titleRef = useRef();
  const history = useNavigate();
  const {
    providerid: routeProviderId,
    providername: routeProviderName,
    category: routeCategory,
  } = useParams();
  const providerid = providerIdProp || routeProviderId;
  const providername = providerNameProp || routeProviderName;
  const category = categoryProp || routeCategory;
  const isMigration = category === "Migration";
  const entityLabel = isMigration ? "Application" : "Course";
  const [nameError, setNameError] = useState("");
  const [intakeError, setIntakeError] = useState("");
  const [intakes, setIntakes] = useState([]);
  const [levels, setLevels] = useState([]);
  const [selectedOption, setSelectedOption] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState("");
  const [prerequisit, setPrerequist] = useState([]);
  const [discipline, setDiscipline] = useState([]);
  const [level, setLevel] = useState("");
  const [name, setName] = useState("");
  const [fee, setFee] = useState("");
  const [professionalCost, setProfessionalCost] = useState("");
  const [departmentFee, setDepartmentFee] = useState("");
  const [professionalCostError, setProfessionalCostError] = useState("");
  const [departmentFeeError, setDepartmentFeeError] = useState("");
  const [levelError, setLevelError] = useState("");

  useEffect(() => {
    if (!embedded) {
      document.title = `Add New ${entityLabel}`;
    }
    getAllIntakes();
    getLevels();
  }, [embedded, entityLabel]);

  const getAllIntakes = useCallback(async () => {
    const result = await getIntakes();
    if (result.data) {
      setIntakes(getCourseFormIntakeOptions(result.data.data));
    }
  }, []);

  const getLevels = async () => {
    get_ApiManager(
      "",
      "levelsByCategory",
      (res) => {
        setLevels(
          []
            .concat(res.data["data"])
            .sort((a, b) => (a.title > b.title ? 1 : -1)),
        );
      },
      (err) => {
        console.log(err);
      },
    );
  };

  const handleValidation = () => {
    setNameError("");
    setIntakeError("");
    setLevelError("");
    setProfessionalCostError("");
    setDepartmentFeeError("");

    if (!name) {
      setNameError("Please enter name");
      window.scrollTo(0, titleRef);
      return false;
    }

    if (professionalCost === "" || professionalCost === null) {
      setProfessionalCostError("Please enter professional cost");
      return false;
    }

    if (isMigration && (departmentFee === "" || departmentFee === null)) {
      setDepartmentFeeError("Please enter department fee");
      return false;
    }

    if (!isMigration && !level) {
      setLevelError("Please select a level");
      return false;
    }

    return true;
  };

  const handleCancel = () => {
    if (embedded && onCancel) {
      onCancel();
      return;
    }
    history(`/providers/provider-details/${providerid}`);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (handleValidation()) {
      const data = {
        providerid: providerid,
        coursename: name,
        prerequisites: prerequisit,
        discipline: discipline,
        duration: selectedDuration,
        tution_fee: fee,
        professional_cost: professionalCost,
        department_fee: departmentFee,
        level: level,
        intakes: selectedOption,
      };
      onSavingChange?.(true);
      post_ApiManager(
        data,
        "courses",
        (res) => {
          onSavingChange?.(false);
          if (res.data.error) {
            swal(" ", res.data.error, "error");
          } else {
            swal("Success!", res.data.message, "success");
            if (embedded && onSuccess) {
              onSuccess();
            } else {
              history(`/providers/provider-details/${providerid}`);
            }
          }
        },
        (err) => {
          onSavingChange?.(false);
          console.log(err);
        },
      );
    }
  };

  const ui = getProviderFormLayout(embedded);
  const disabledInputClass = embedded
    ? `${ui.input} cursor-not-allowed bg-gray-50 text-gray-500`
    : `${ui.input} cms-provider-add-input--disabled`;
  const hintClass = embedded
    ? "mt-1 text-xs text-gray-400"
    : "cms-provider-add-hint";

  return (
    <div className={ui.wrap}>
      {!embedded && (
        <button type="button" onClick={handleCancel} className={ui.backBtn}>
          <FaChevronLeft size={10} />
          Back to Provider
        </button>
      )}

      <div className={ui.inner}>
        {!embedded && (
          <div className={ui.cardHeader}>
            <h1 className={ui.cardTitle}>ADD New {entityLabel}</h1>
          </div>
        )}

        <form
          id={formId}
          ref={titleRef}
          className={ui.form}
          onSubmit={onSubmit}
        >
          <div className={ui.section}>
            <div
              className={ui.gridClass}
              {...(ui.gridStyle ? { style: ui.gridStyle } : {})}
            >
              <div className={ui.field}>
                <label className={ui.label}>Provider Name</label>
                <div className={ui.control}>
                  <input
                    type="text"
                    disabled
                    defaultValue={providername}
                    className={disabledInputClass}
                  />
                </div>
              </div>

              <div className={ui.field}>
                <label className={ui.label}>
                  {entityLabel} Name <span className={ui.requiredMark}>*</span>
                </label>
                <div className={ui.control}>
                  <input
                    type="text"
                    name="name"
                    onChange={(e) => setName(e.target.value)}
                    className={ui.input}
                    placeholder="Please Enter"
                  />
                </div>
                {nameError && <small className={ui.error}>{nameError}</small>}
              </div>

              <div className={ui.field}>
                <label className={ui.label}>
                  Professional Cost <span className={ui.requiredMark}>*</span>
                </label>
                <div className={ui.control}>
                  <input
                    type="number"
                    className={ui.input}
                    placeholder="Please Enter"
                    value={professionalCost}
                    onChange={(e) => setProfessionalCost(e.target.value)}
                  />
                </div>
                {professionalCostError && (
                  <small className={ui.error}>{professionalCostError}</small>
                )}
              </div>

              {isMigration && (
                <div className={ui.field}>
                  <label className={ui.label}>
                    Department Fee <span className={ui.requiredMark}>*</span>
                  </label>
                  <div className={ui.control}>
                    <input
                      type="number"
                      className={ui.input}
                      placeholder="Please Enter"
                      value={departmentFee}
                      onChange={(e) => setDepartmentFee(e.target.value)}
                    />
                  </div>
                  {departmentFeeError && (
                    <small className={ui.error}>{departmentFeeError}</small>
                  )}
                </div>
              )}

              {!isMigration && (
                <>
                  <div className={ui.field}>
                    <label className={ui.label}>
                      Level <span className={ui.requiredMark}>*</span>
                    </label>
                    <div className={ui.control}>
                      <select
                        onChange={(e) => setLevel(e.target.value)}
                        value={level || ""}
                        name="level"
                        className={ui.select}
                      >
                        <option value="">Please Select</option>
                        {levels.map((lvl) => (
                          <option key={lvl.id} value={lvl.id}>
                            {lvl.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    {levelError && (
                      <small className={ui.error}>{levelError}</small>
                    )}
                  </div>

                  <div className={ui.field}>
                    <label className={ui.label}>InTakes</label>
                    <div className={ui.controlMulti}>
                      <ProviderFormMultiselect
                        options={intakes}
                        placeholder="Intakes"
                        displayValue="label"
                        selectedValues={selectedOption}
                        onSelect={setSelectedOption}
                        onRemove={setSelectedOption}
                        className={embedded ? "w-full" : ""}
                      />
                    </div>
                    {intakeError && (
                      <small className={ui.error}>{intakeError}</small>
                    )}
                  </div>

                  <div className={ui.field}>
                    <label className={ui.label}>Prerequisites</label>
                    <div className={ui.controlMulti}>
                      <ProviderFormMultiselect
                        options={prerequisites}
                        groupBy="group"
                        placeholder="Prerequisites"
                        displayValue="title"
                        selectedValues={prerequisit}
                        onSelect={setPrerequist}
                        onRemove={setPrerequist}
                        className={embedded ? "w-full" : ""}
                      />
                    </div>
                  </div>

                  <div className={ui.field}>
                    <label className={ui.label}>Discipline</label>
                    <div className={ui.controlMulti}>
                      <ProviderFormMultiselect
                        options={genericDiscipline}
                        groupBy="group"
                        placeholder="Discipline"
                        displayValue="title"
                        selectedValues={Array.isArray(discipline) ? discipline : []}
                        onSelect={setDiscipline}
                        onRemove={setDiscipline}
                        className={embedded ? "w-full" : ""}
                      />
                    </div>
                  </div>

                  <div className={ui.field}>
                    <label className={ui.label}>Tution Fee</label>
                    <div className={ui.control}>
                      <input
                        type="number"
                        className={ui.input}
                        placeholder="Please Enter"
                        onChange={(e) => setFee(e.target.value)}
                      />
                    </div>
                    <p className={hintClass}>
                      According to country&apos;s currency
                    </p>
                  </div>

                  <div className={ui.field}>
                    <label className={ui.label}>Duration</label>
                    <div className={ui.control}>
                      <select
                        onChange={(e) => setSelectedDuration(e.target.value)}
                        value={selectedDuration || ""}
                        name="duration"
                        className={ui.select}
                      >
                        <option value="">Please Select</option>
                        {duration.map((value, index) => (
                          <option key={index} value={value.title}>
                            {value.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {!embedded && (
            <div className={ui.footer}>
              <button
                type="button"
                onClick={handleCancel}
                className={ui.cancelBtn}
              >
                Cancel
              </button>
              <button type="submit" className={ui.submitBtn}>
                Add {entityLabel}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default React.memo(AddCourse);
